import {
  LOGO_MAX_BYTES,
  updateBrandingRequestSchema,
  type UpdateBrandingRequest,
} from "@stb/api-contract"
import { createServerFn } from "@tanstack/react-start"

import { isAppError } from "@/core/app-error"

import {
  deleteLogo,
  getBranding,
  updateBranding,
  uploadLogo,
  type BrandingView,
} from "../branding.server"
import { reencodeLogo } from "../logo-reencode.server"
import { getLogoStorage } from "../storage.server"
import { requireInvoicingContext, runAction, type RpcResult } from "./context"

/** Branding settings for the invoicing UI. Reads never throw feature errors; writes return `RpcResult`. */

export const loadBranding = createServerFn({ method: "GET" }).handler(
  async (): Promise<BrandingView> => {
    const { context } = await requireInvoicingContext()
    try {
      return await getBranding(context, getLogoStorage())
    } catch (error) {
      if (isAppError(error)) throw new Error(error.message, { cause: error })
      throw error
    }
  },
)

export const updateBrandingAction = createServerFn({ method: "POST" })
  .validator((data: UpdateBrandingRequest) => updateBrandingRequestSchema.parse(data))
  .handler(async ({ data }): Promise<RpcResult<BrandingView>> =>
    runAction(({ context }) => updateBranding(data, context, getLogoStorage())),
  )

/** Multipart form with one `file` field. The type and size are decided from the bytes, not the name. */
export const uploadLogoAction = createServerFn({ method: "POST" })
  .validator((data: FormData) => {
    const file = data.get("file")
    if (!(file instanceof File)) throw new Error("Choose a logo file")
    // refuse before copying the bytes and calling the image service; the service re-checks the real bytes
    if (file.size > LOGO_MAX_BYTES) throw new Error("The logo must be 1 MB or smaller")
    return file
  })
  .handler(async ({ data }): Promise<RpcResult<BrandingView>> =>
    runAction(async ({ context }) =>
      uploadLogo(new Uint8Array(await data.arrayBuffer()), context, {
        reencode: reencodeLogo,
        storage: getLogoStorage(),
      }),
    ),
  )

export const deleteLogoAction = createServerFn({ method: "POST" }).handler(
  async (): Promise<RpcResult<BrandingView>> =>
    runAction(({ context }) => deleteLogo(context, getLogoStorage())),
)
